import { DrugIcd, Prescription } from "@fusion-advantage/backcompat"
import { DrugIcdCodeDataSet, medReconItemCanRemove, medReconItemCanReset, medReconItemIsReconciled, medReconItemMustConfirm, newPrescription } from "@fusion-advantage/formlib"
import L2Erf from "../l2/L2Erf"
import { L2ErfRow, L2ErfRowContent } from "../l2/L2ErfRow"
import { L1MedicationReconcilliationTableIndications, L1MedicationReconcilliationTableRow, L1MedicationReconcilliationTableInUse, L1MedicationReconcilliationTableName, L1MedicationReconcilliationTableReason, L1MedicationReconcilliationTableSource, ActionRequiredRowIndicator, ScanOrManuallyEnterWarning, MedicationReconciliationRowActions } from "../l1/L1MedicationReconcilliation"
import { useCallback, useEffect, useMemo, useState } from "react"
import _ from 'lodash'
import { FusionColor } from "../../../theme/FusionStyle"
import { L1SmallIconButton } from "../l1/L1Button"
import { faPen } from "@fortawesome/pro-solid-svg-icons"
import L3MedReconIndicationsModal from "./L3MedReconIndicationsModal"
import styled, { CSSObject } from "@emotion/styled"

type L3MedicationReconciliationTableProps = {
	models: Prescription[],
	onChange: (e: Prescription[]) => void,
	handleLoadProductCodes: (productId: number) => Promise<DrugIcd[]>,
	icdDataSet: DrugIcdCodeDataSet,
}

type L3MedicationReconciliationReadOnlyTableProps = {
	models: Prescription[],
}

type MedicationReconciliationTableRowProps = {
	model: Prescription,
	onChange: (m: Prescription) => void,
	handleClickAddIndications: () => void,
	onResetRow: (e: Prescription) => void,
	onRemoveRow: () => void,
	showDivider: boolean,
}

type MedicationReconciliationTableReadOnlyRowProps = {
	model: Prescription,
	showDivider: boolean,
}

type MedReconModalState = {
	show: boolean,
	productId: number|null,
}

const L3MedicationReconciliationTableContainer = styled.div(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	maxHeight: '38vh',
	overflow: 'hidden',
	boxSizing: 'border-box',
	borderRadius: theme.borderRadius,
	border: `1px solid ${theme.colors.lines}`,
}))

const L3MedicationReconciliationTableContent = styled.div(({theme}): CSSObject => ({
	height: '100%',
	overflowY: 'auto',
	borderRadius: theme.borderRadius,
	background: `
		linear-gradient(#fff 33%, rgba(82, 52, 52, 0)),
		linear-gradient(rgba(255,255,255, 0), #fff 66%) 0 100%,
		linear-gradient(rgba(50,50,50, 0.5), rgba(0,0,0,0)),
		linear-gradient(rgba(0,0,0,0), rgba(50,50,50, 0.15)) 0 100%;
	`,
	backgroundColor: '#fff',
	backgroundRepeat: 'no-repeat',
	backgroundAttachment: 'local, local, scroll, scroll',
	backgroundSize: '100% 100px, 100% 45px, 100% 60px, 100% 15px',
}))

const IndicationsEditCell = styled(L2ErfRowContent)({
	paddingRight: 0,
})

const MedicationReconciliationTableReadOnlyRow = ({model, showDivider}: MedicationReconciliationTableReadOnlyRowProps) => {
	return (
		<L1MedicationReconcilliationTableRow key={model.Product.ID} showDivider={showDivider}>
			<L2ErfRowContent justifyContent="center">
				<L1MedicationReconcilliationTableSource model={model} reconciled={false} />
			</L2ErfRowContent>
			<L2ErfRowContent justifyContent="center">
				<L1MedicationReconcilliationTableName>{model.Product.Name}</L1MedicationReconcilliationTableName>
			</L2ErfRowContent>
			<L2ErfRowContent justifyContent="center">
				{model.InUse === true ? "Yes" : model.InUse === false ? "No" : "Null"}
			</L2ErfRowContent>
			<L2ErfRowContent justifyContent="center">
				{model.Reason}
			</L2ErfRowContent>
			<L2ErfRowContent justifyContent="center">
				{
					(model.Indications.length > 0) && (
						<L1MedicationReconcilliationTableIndications model={model} reconciled={false} onAddIndication={() => {}} />
					) 
				}
			</L2ErfRowContent>
		</L1MedicationReconcilliationTableRow>
	)
}

const MedicationReconciliationTableRow = ({model, onChange, handleClickAddIndications, onResetRow, onRemoveRow, showDivider}: MedicationReconciliationTableRowProps) => {
	const reconciled = medReconItemIsReconciled(model)
	const mustConfirmDrug = medReconItemMustConfirm(model)
	const canReset = medReconItemCanReset(model)
	const canRemove = medReconItemCanRemove(model)

	return (
		<L1MedicationReconcilliationTableRow key={model.Product.ID} isReconciled={reconciled} showDivider={showDivider}>
			<ActionRequiredRowIndicator satisfied={reconciled} />
			<L2ErfRowContent justifyContent="center">
				<L1MedicationReconcilliationTableSource model={model} reconciled={reconciled} />
			</L2ErfRowContent>
			<L2ErfRowContent justifyContent="center">
				<L1MedicationReconcilliationTableName>{model.Product.Name}</L1MedicationReconcilliationTableName>
			</L2ErfRowContent>
			<L2ErfRowContent justifyContent="center">
				<L1MedicationReconcilliationTableInUse model={model} reconciled={reconciled} onChange={onChange} />
			</L2ErfRowContent>
			{mustConfirmDrug ? (
				<ScanOrManuallyEnterWarning />
			) : (
				<>
					<L2ErfRowContent justifyContent="center">
						<L1MedicationReconcilliationTableReason model={model} onChange={onChange} reconciled={reconciled} />
					</L2ErfRowContent>
					<L2ErfRowContent justifyContent="center">
						<L1MedicationReconcilliationTableIndications model={model} reconciled={reconciled} onAddIndication={handleClickAddIndications} />
					</L2ErfRowContent>
					<IndicationsEditCell justifyContent="center">
						{
							(model.Indications.length > 0) && (
								<L1SmallIconButton color={FusionColor.BlueMain} icon={faPen} onClick={handleClickAddIndications} />
							)
						}
					</IndicationsEditCell>
				</>
			)}
			<MedicationReconciliationRowActions 
				model={model}
				reconciled={reconciled}
				canReset={canReset}
				canRemove={canRemove}
				onReset={() => onResetRow(model)}
				onRemove={onRemoveRow}
			/>
		</L1MedicationReconcilliationTableRow>
	)
}

export const L3MedicationReconciliationReadOnlyTable = ({models}: L3MedicationReconciliationReadOnlyTableProps) => {
	const sortedModels = useMemo(() => {
		return _.orderBy(models, ['InUse', 'Product.Name'], ['asc', 'asc'])
	}, [models])

	return (
		<L2Erf
			hideControlTitleColumn={true}
			controlTemplateColumns="auto"
			contentTemplateColumns="auto auto auto auto auto auto"
			contentTitles={['Source', 'Name', 'In Use?', 'Reason for not Taking', 'Reason for Use']}
		>
			{sortedModels.map((item, idx) => {
				return (
					<MedicationReconciliationTableReadOnlyRow 
						model={item}
						showDivider={idx < sortedModels.length - 1}
						key={item.Product.ID}
					/>
				) 
			})}
		</L2Erf>
	)
}

const contentTemplateColumns = [
	'max-content',                // source icon
	'minmax(12rem, 1fr)',         // name: the most important detail, make at least minimal room!
	'max-content',                // in use buttons
	'minmax(4rem, max-content)',  // reason select / "please scan..." minmax() important for label to wrap!
	'1fr',                        // indications
	'max-content',                // edit indications button
	'max-content',                // delete button
].join(' ')

const L3MedicationReconciliationTable = ({models: origModels, handleLoadProductCodes, onChange, icdDataSet}: L3MedicationReconciliationTableProps) => {
	const [models, setModels] = useState(_.cloneDeep(origModels))
	const [modalState, setModalState] = useState<MedReconModalState>({ show: false, productId: null })

	useEffect(() => {
		setModels(_.cloneDeep(origModels))
	}, [origModels])

	const handleChange = useCallback((model: Prescription) => {
		const modelsTemp = _.cloneDeep(models)
		const index = modelsTemp.findIndex(x => x.Product.ID === model.Product.ID)

		if(index > -1) {
			modelsTemp.splice(index, 1, model)
	
			onChange(modelsTemp)
		}
	}, [models, onChange])

	const handleSaveIndications = useCallback((m: Prescription[]) => {
		onChange(m)
	}, [onChange])

	const handleRowReset = useCallback((model: Prescription) => {
		const modelsTemp = _.cloneDeep(models)
		const index = modelsTemp.findIndex(x => x.Product.ID === model.Product.ID)
		
		if(index > -1) {
			modelsTemp.splice(index, 1, {
				...newPrescription(model.Product),
				IsPrior: model.IsPrior,
			})
			onChange(modelsTemp)
		}
	}, [models, onChange])

	const handleRowRemove = useCallback((id: number) => {
		const modelsTemp = _.cloneDeep(models)
		const index = modelsTemp.findIndex(x => x.Product.ID === id)
		
		if(index > -1) {
			modelsTemp.splice(index, 1)
	
			onChange(modelsTemp)
		}
	}, [models, onChange])

	const handleShowModal = useCallback((productId: number|null) => {
		setModalState({show: true, productId })
	}, [])

	const handleCloseModal = useCallback(() => {
		setModalState({show: false, productId: null})
	}, [])

	return (
		<L3MedicationReconciliationTableContainer>
			<L3MedReconIndicationsModal
				handleLoadProductCodes={handleLoadProductCodes}
				models={models}
				show={modalState.show}
				initialId={modalState.productId}
				onClose={handleCloseModal}
				icdDataSet={icdDataSet}
				onSave={handleSaveIndications}
			/>
			<L3MedicationReconciliationTableContent>
				<L2Erf
					hideControlTitleColumn={true}
					controlTemplateColumns="8px"
					contentTemplateColumns={contentTemplateColumns}
					contentTitles={['', 'Source', 'Name', 'In Use?', 'Reason for not Taking', 'Reason for Use']}
					outlineHeader
				>
					{models.map((item, idx) => {
						const productId = item.Product.ID

						return (
							<MedicationReconciliationTableRow
								key={idx}
								model={item}
								onChange={(m) => handleChange(m)}
								handleClickAddIndications={() => handleShowModal(productId)}
								onRemoveRow={() => handleRowRemove(productId)}
								onResetRow={(m) => handleRowReset(m)}
								showDivider={idx < models.length - 1}
							/>
						)
					})}
				</L2Erf>
			</L3MedicationReconciliationTableContent>
		</L3MedicationReconciliationTableContainer>
	)
}

export default L3MedicationReconciliationTable